import React, { useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { FormikProps, useFormik } from 'formik';
import { Close, Help, JoyAi, Trash, WarningTriangle } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { pxToRem } from '@withjoy/joykit/theme';
import { Flex, FormControl, TextV2, TextArea, Box, useToast, TooltipV2, Spinner } from '@withjoy/joykit';
import { useAuth } from '@shared/components/AuthProvider';
import { PhotoCarousel } from '@shared/components/PhotoCarousel';
import { DEFAULT_HOTEL_IMAGE_WIDE } from '@shared/utils/constants';
import { calculateStayDates, createDateFromUnformattedString } from '@shared/utils/dateHelpers';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useHotelRedirectUrl } from '@apps/admin/routes/Planning/routes/Accommodations/hooks/useHotelRedirectUrl';
import { DistanceFromVenue } from '@shared/components/DistanceFromVenue';
import { AccommodationPlaceType, AiModelVersion, useChatGPTTextCompletionQuery } from '@graphql/generated';
import { HotelRatingV2 } from '@shared/components/HotelRatingV2';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { useRemoteFeatureFlag } from '@shared/core/featureFlags/remoteFeatureFlags';
import { addError } from '@shared/utils/logger';
import { getChatGPTTextCompletionPromptForHotel, getFormattedChatGPTText, getHotelAddress } from '../../utils/Accommodations.utils';
import { TravelMapBlockType } from '../../Accommodations.types';
import { HotelRoomBlockForm, useHotelRoomBlockFormSchema } from '../HotelRoomBlockForm';
import { HotelFormFields, HotelFormProps } from '../../pages/Hotel/HotelForm.types';
import { useAdminAccommodationsTelemetry } from '../../Accommodations.telemetry';
import { StarRatingWithTooltip } from '../StarRatingWithTooltip';
import StyledToggle from '../StyledToggle';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { SaveCancelButtons } from '@website-editor/features/custom-page/components/SaveCancelButtons/SaveCancelButtons';
import { animationTransition } from '@shared/utils/animationTransition';

const MAX_LENGTH = 1500;
const ASPECT_RATIO = '3 / 2';

export const HotelForm: React.FC<HotelFormProps> = props => {
  const { hotelData: hotelData, isLoading, isEditMode, isInDialog, handleCancel, handleSubmit, handleDelete, containerId, handleCancelRoomBlock } = props;
  const {
    value: accommodationPlaceUserNote,
    loading: loadingAccommodationPlaceUserNote,
    payload: accommodationPlaceUserNotePayload,
  } = useRemoteFeatureFlag('accommodationPlaceUserNote');

  const { selectedHotelPhotos } = useCustomComponentStateContext();
  const [isGeneratedMessageLoading, setIsGeneratedMessageLoading] = useState(false);
  const { eventInfo } = useEventInfo();
  const { checkIn, checkOut } = calculateStayDates(eventInfo?.finalizedDate?.dateString);
  const { toast } = useToast();
  const { tq } = useTranslation('travelMap');

  const { isSuperAdmin } = useAuth();

  const address =
    hotelData.customAddress ||
    getHotelAddress({ address: hotelData.address, city: hotelData.city, state: hotelData.state, postalCode: hotelData.postalCode, country: hotelData.country });

  const telemetry = useAdminAccommodationsTelemetry();
  useEffect(() => {
    if (isEditMode) {
      telemetry.editItemClicked('accommodations.AccommodationPlaces', {
        itemType: TravelMapBlockType.travelMapAccommodationsHotel,
        isSuperAdmin,
        zhId: hotelData.zhId,
        displayName: hotelData.name,
        customName: hotelData.customName,
        url: hotelData.url,
        customUrl: hotelData.customUrl,
        accommodationAction: 'accommodationEdit',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hotelRoomBlockSchema = useHotelRoomBlockFormSchema();
  const formik = useFormik<HotelFormFields>({
    initialValues: {
      name: hotelData.customName || hotelData.name,
      address: address,
      phone: hotelData.customPhone || hotelData.phone,

      customName: hotelData.customName || hotelData.name,
      customAddress:
        hotelData.customAddress ||
        getHotelAddress({ address: hotelData.address, city: hotelData.city, state: hotelData.state, postalCode: hotelData.postalCode, country: hotelData.country }),
      customPhone: hotelData.customPhone || hotelData.phone,

      url: hotelData.customUrl,
      postalCode: hotelData.postalCode,
      country: hotelData.country,
      latitude: hotelData.lat,
      longitude: hotelData.long,
      note: hotelData.userNote,
      customPhotoId: hotelData.customPhoto?.id ?? undefined,
      zhId: hotelData.zhId,
      joyPlaceId: hotelData.joyPlaceId,
      googlePlaceId: hotelData.googlePlaceId,

      isFavorite: hotelData.isFavorite || false,
      isStayingHere: hotelData.isHostStayingHere || false,
      isHotelRoomBlock: !!hotelData.accommodationRoomBlock,
      photoUrl: hotelData.photo?.url ?? undefined,
      bookingUrl: hotelData.accommodationRoomBlock?.bookingUrl || '',
      isBookingLinkDisabled: !hotelData.accommodationRoomBlock?.bookingUrl,
      isJoyManagedRoomBlock: Boolean(hotelData.accommodationRoomBlock?.isJoyManagedRoomBlock),
      checkInDate: hotelData.accommodationRoomBlock?.checkInDate
        ? createDateFromUnformattedString(hotelData.accommodationRoomBlock?.checkInDate)
        : eventInfo?.finalizedDate?.dateString
          ? checkIn.date
          : undefined,
      checkOutDate: hotelData.accommodationRoomBlock?.checkOutDate
        ? createDateFromUnformattedString(hotelData.accommodationRoomBlock?.checkOutDate)
        : eventInfo?.finalizedDate?.dateString
          ? checkOut.date
          : undefined,
      cutoffDate: hotelData.accommodationRoomBlock?.cutoffDate ? createDateFromUnformattedString(hotelData.accommodationRoomBlock?.cutoffDate) : undefined,
      numberOfRoomsReserved: hotelData.accommodationRoomBlock?.numberOfRoomsReserved || 0,
      strikeoutPricePerNight: hotelData.accommodationRoomBlock?.strikeoutPricePerNight || 0,
      pricePerNight: hotelData.accommodationRoomBlock?.pricePerNight || 0,
    },
    validationSchema: Yup.object({
      name: Yup.string().required(tq`Please search for a hotel`),
      address: Yup.string().required(tq`Please add an address`),

      isFavorite: Yup.boolean(),
      isStayingHere: Yup.boolean(),
      isHotelRoomBlock: Yup.boolean(),
      ...hotelRoomBlockSchema,
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: values => {
      if (!(values.zhId || values.joyPlaceId || values.googlePlaceId)) {
        const selectHotelError = tq`An error occurred while selecting the hotel. Please try again.`;
        toast(selectHotelError, { icon: <WarningTriangle /> });
        formik.setFieldError('name', selectHotelError);
        return;
      }

      handleSubmit({ ...values, customUrl: null });
      telemetry.saveButtonClicked('accommodations', {
        itemType: TravelMapBlockType.travelMapAccommodationsHotel,
        accommodationAction: isEditMode ? 'edited' : 'added',
      });
    },
  });

  const handleTextCompletion = useCallback(
    (textCompletion: string | null | undefined) => {
      if (textCompletion) {
        const note = getFormattedChatGPTText(textCompletion);
        formik.setFieldValue('note', note);
      }
    },
    [formik],
  );

  const promptError = tq`We're sorry, we're having trouble generating a prompt. Try again`;

  const { loading: initialLoading, refetch } = useChatGPTTextCompletionQuery({
    variables: {
      prompt:
        accommodationPlaceUserNote == 'on' && (accommodationPlaceUserNotePayload as { prompt: string })?.prompt
          ? `${(accommodationPlaceUserNotePayload as { prompt: string }).prompt} ${hotelData.name || hotelData.customName || ''}`
          : getChatGPTTextCompletionPromptForHotel(hotelData.name || hotelData.customName || ''),
      modelVersion: AiModelVersion.gpt35turboInstruct,
    },
    batchMode: 'off',
    skip: isEditMode || loadingAccommodationPlaceUserNote,
    onCompleted: ({ textCompletion }) => handleTextCompletion(textCompletion),
    onError: error => {
      addError(error.message, { context: 'AdminHotelForm chatGPTTextCompletionQuery' });
      toast(promptError, { icon: <WarningTriangle /> });
    },
  });

  const onSuggestNoteClick = useCallback(() => {
    setIsGeneratedMessageLoading(true);
    refetch()
      .then(({ data }) => {
        handleTextCompletion(data?.textCompletion);
      })
      .catch(() => {
        toast(promptError, { icon: <WarningTriangle /> });
      })
      .finally(() => {
        setIsGeneratedMessageLoading(false);
      });
  }, [refetch, handleTextCompletion, toast, promptError]);

  const getLengthOfStringField = (key: string): number => {
    const value = formik.values[key];
    if (value && typeof value === 'string') {
      return value.length;
    }
    return 0;
  };

  // @todo - Get multiple images from BE
  const imagesForCarousel: { src: string; alt: string }[] = [];
  if (hotelData.customPhoto?.url) {
    imagesForCarousel.push({
      src: hotelData.customPhoto.url,
      alt: hotelData.name || '',
    });
  }
  if (selectedHotelPhotos?.length) {
    selectedHotelPhotos?.forEach(hotelPhoto => {
      imagesForCarousel.push({ src: hotelPhoto.url, alt: hotelData.name || '' });
    });
  } else if (hotelData.photos?.length) {
    hotelData.photos.forEach((photo: string) => {
      imagesForCarousel.push({ src: photo, alt: hotelData.name || '' });
    });
  } else if (hotelData.photo?.url) {
    imagesForCarousel.push({
      src: hotelData.photo.url,
      alt: hotelData.name || '',
    });
  }
  if (!hotelData.customPhoto?.url && !hotelData.photo?.url) {
    imagesForCarousel.push({ src: DEFAULT_HOTEL_IMAGE_WIDE, alt: hotelData.name || '' });
  }

  const type = hotelData.type === AccommodationPlaceType.hotel ? TravelMapBlockType.travelMapAccommodationsHotel : TravelMapBlockType.travelMapAccommodationsCustom;
  const hotelUrl = useHotelRedirectUrl({
    hotel: { ...hotelData, type },
    searchParams: {
      skipAvailabilityCheck: 'true',
      utm_medium: 'web',
      utm_source: 'admin_accommodations',
      utm_campaign: 'hotel_form',
    },
  });

  // Hotel data for user rating
  const hotelForRating = {
    joyPlaceId: hotelData.joyPlaceId,
    zhId: hotelData.zhId,
    type,
  };

  const linkProps = hotelUrl ? ({ as: 'a', href: hotelUrl, target: '_blank', rel: 'noopener noreferrer' } as const) : ({ as: 'div' } as const);

  const handleSave = useEventCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    formik.submitForm();
  });

  return (
    <form
      onSubmit={formik.handleSubmit}
      style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', position: 'relative' }}
    >
      <Box {...linkProps}>
        <Box
          typographyVariant="hed3"
          textDecorationColor="white"
          _hover={{ textDecoration: 'underline', textDecorationColor: 'mono7' }}
          transition={animationTransition('text-decoration', 'color')}
          css={{ 'text-underline-offset': pxToRem(3) }}
        >
          {hotelData.customName || hotelData.name}
        </Box>
        <Flex
          justifyContent="space-between"
          alignItems="flex-end"
          marginTop={3}
        >
          <Box width="60%">
            <Flex
              gap={5}
              alignItems="center"
              flexWrap="wrap"
            >
              <StarRatingWithTooltip starRating={hotelData.starRating} />
              <DistanceFromVenue
                distances={hotelData.distances}
                milesToVenue={hotelData.distance}
              />
            </Flex>
            {hotelUrl && (
              <TextV2
                marginTop={4}
                typographyVariant="label4"
                color="newUIBrandPrimary"
                borderBottom="1px dotted"
                borderBottomColor="newUIBrandPrimary"
                _hover={{ cursor: 'pointer' }}
                fontSize={pxToRem(14)}
                fontWeight={500}
                width="fit-content"
              >
                {tq`Explore This Property →`}
              </TextV2>
            )}
          </Box>
          <HotelRatingV2 hotel={hotelForRating} />
        </Flex>
      </Box>

      <Flex
        gap={1}
        alignItems="center"
        marginTop={7}
      >
        <TextV2 typographyVariant="button2">{tq`Thumbnail`}</TextV2>
        <TooltipV2
          overrides={{
            Content: { props: { paddingY: 4, paddingLeft: pxToRem(14), paddingRight: 4, borderRadius: pxToRem(12), color: 'white' } },
          }}
          content={
            <Flex
              flexDirection="column"
              maxWidth={pxToRem(256)}
            >
              <Box typographyVariant="label3">{tq`Thumbnail`}</Box>
              <Box
                typographyVariant="label2"
                color="mono4"
              >
                {tq`This is a preview of how this hotel will be displayed on your website. Guests click on this to view more details and book from your website.`}
              </Box>
            </Flex>
          }
          onMouseEnterDelay={500}
        >
          <Help
            size={16}
            color="mono10"
          />
        </TooltipV2>
      </Flex>
      <Box
        width="100%"
        height="100%"
        marginTop={5}
      >
        <Box
          aspectRatio={ASPECT_RATIO}
          position={'relative'}
          backgroundColor={'mono2'}
          overflow={'hidden'}
          borderRadius={pxToRem(12)}
        >
          <PhotoCarousel
            images={imagesForCarousel}
            aspectRatio={ASPECT_RATIO}
            navigation={{ variant: 'minimal', visible: 'default' }}
          />
        </Box>
      </Box>
      <FormControl
        marginTop={7}
        label={
          <Flex justifyContent="space-between">
            <Flex
              gap={1}
              alignItems="center"
            >
              <TextV2 typographyVariant="button2">{tq`Why It’s Your Pick`}</TextV2>
              <TooltipV2
                overrides={{
                  Content: { props: { paddingY: 4, paddingLeft: pxToRem(14), paddingRight: 4, borderRadius: pxToRem(12), color: 'white' } },
                }}
                content={
                  <Flex
                    flexDirection="column"
                    maxWidth={pxToRem(256)}
                  >
                    <Box typographyVariant="label3">{tq`Tell Guests Why`}</Box>
                    <Box
                      typographyVariant="label2"
                      color="mono4"
                    >
                      {tq`Write a note to guests about why you picked this hotel. Let them know any details you might want to share.`}
                    </Box>
                  </Flex>
                }
                onMouseEnterDelay={500}
              >
                <Help
                  size={16}
                  color="mono10"
                />
              </TooltipV2>
            </Flex>
            {isGeneratedMessageLoading || initialLoading ? (
              <Spinner />
            ) : (
              <Flex
                gap={3}
                alignItems="center"
                _hover={{ cursor: 'pointer' }}
                onClick={onSuggestNoteClick}
              >
                <JoyAi
                  size={16}
                  color="newUITest1"
                />
                <TextV2
                  typographyVariant="label3"
                  color="newUITest1"
                >
                  {tq`Suggest`}
                </TextV2>
              </Flex>
            )}
          </Flex>
        }
        isRequired={false}
        caption={`${getLengthOfStringField('note')}/${MAX_LENGTH}`}
        overrides={{ Hint: { props: { color: 'mono10', textAlign: 'right' } } }}
      >
        <Flex
          __css={{
            ['& > div']: {
              width: '100% !important',
              _after: {
                border: '0px solid transparent',
                borderRadius: pxToRem(4),
                boxShadow: '0px 0 0px 2px #5f269b',
                bottom: 0,
                left: 0,
                top: 0,
                right: 0,
                height: 'calc(100% - 4px)',
                width: '100%',
              },
            },
            ['textarea']: {
              color: '#333333',
              outline: 0,
              borderRadius: pxToRem(4),
              border: '0px solid',
              boxShadow: '0px 0 0px 1px #D9D9D9',
              _focus: { outline: 0, _hover: { border: '0px solid' } },
              _hover: { border: '0px solid', boxShadow: '0px 0 0px 1px #BEBEBE' },
              fontSize: `${pxToRem(16)} !important`,
              maxHeight: pxToRem(144),
            },
          }}
          paddingTop={3}
        >
          <TextArea
            minRows={4}
            resize={true}
            maxLength={MAX_LENGTH}
            {...formik.getFieldProps('note')}
          />
        </Flex>
      </FormControl>
      <StyledToggle
        label={tq`Spotlight`}
        sideNote={tq`Pick one hotel that will be displayed at the top of the list.`}
        name="isFavorite"
        overrides={{ Root: { props: { marginTop: 7 } } }}
        tooltip={{
          title: tq`Mark as Spotlight`,
          content: tq`Marking a hotel as a favorite lets guests know you prefer them to book here. Your hotel will show a “favorites” badge on the link.`,
        }}
        isChecked={!!formik.values.isFavorite}
        onChange={() => formik.setFieldValue('isFavorite', !formik.values.isFavorite)}
      />
      <StyledToggle
        label={tq`We’re Staying Here`}
        sideNote={tq`Let your guests know where you're staying.`}
        name="isStayingHere"
        overrides={{ Root: { props: { marginTop: 6, marginBottom: isEditMode && formik.values.isHotelRoomBlock ? 6 : 0 } } }}
        tooltip={{
          title: tq`Where You’re Staying`,
          content: tq`Let guests know where you’ll be staying. We’ll put this hotel first on your page and highlight it letting guests know with a label.`,
        }}
        isChecked={!!formik.values.isStayingHere}
        onChange={() => formik.setFieldValue('isStayingHere', !formik.values.isStayingHere)}
      />
      {!isEditMode && (
        <StyledToggle
          label={tq`Hotel Room Block`}
          name="isHotelRoomBlock"
          overrides={{ Root: { props: { marginTop: 6, marginBottom: 7 } } }}
          tooltip={{ title: tq`Room Block`, content: tq`Let guests know this is a group room block. You’ll be able to set the booking deadline and discount price.` }}
          isChecked={!!formik.values.isHotelRoomBlock}
          onChange={() => formik.setFieldValue('isHotelRoomBlock', !formik.values.isHotelRoomBlock)}
        />
      )}
      {formik.values.isHotelRoomBlock && (
        <HotelRoomBlockForm
          type={TravelMapBlockType.travelMapAccommodationsHotel}
          formik={formik as FormikProps<HotelFormFields>}
          containerId={containerId}
          data={hotelData}
          isInDialog={isInDialog}
          isEditMode={isEditMode}
        />
      )}
      {isEditMode && (
        <Box>
          {handleDelete && (isSuperAdmin || !formik?.values?.isJoyManagedRoomBlock) && (
            <Flex
              justifyContent="center"
              alignItems="center"
              marginTop={8}
            >
              <Flex
                gap={3}
                cursor="pointer"
                alignItems="center"
                onClick={() => handleDelete()}
              >
                <Trash
                  size={24}
                  color="negative6"
                />
                <TextV2
                  typographyVariant="button2"
                  color="negative6"
                >
                  {tq`Remove Stay from Page`}
                </TextV2>
              </Flex>
            </Flex>
          )}
          {formik?.values?.isJoyManagedRoomBlock && (
            <Flex
              justifyContent="center"
              alignItems="center"
              marginTop={6}
            >
              <Flex
                gap={3}
                cursor="pointer"
                alignItems="center"
                onClick={handleCancelRoomBlock}
              >
                <Close
                  size={24}
                  color="negative6"
                />
                <TextV2
                  typographyVariant="button2"
                  color="negative6"
                >
                  {tq`Cancel Room Block`}
                </TextV2>
              </Flex>
            </Flex>
          )}
        </Box>
      )}
      <SaveCancelButtons
        onCancel={handleCancel}
        onSave={handleSave}
        isLoading={isLoading}
      />
    </form>
  );
};
